import { supabase } from '../lib/supabase.js';

class BackgroundTaskService {
  constructor() {
    // Direct database operations instead of Edge Functions
  }

  /**
   * Create a new background task
   * @param {string} type - Task type ('website_analysis' or 'email_verification')
   * @param {object} data - Task data
   * @returns {Promise<object>} Task object
   */
  async createTask(type, data) {
    try {
      const taskData = {
        type,
        status: 'pending',
        data,
        progress: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: task, error } = await supabase
        .from('background_tasks')
        .insert(taskData)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create task: ${error.message}`);
      }

      // Start processing the task immediately (simulate background processing)
      this.processTaskAsync(task.id);

      return task;
    } catch (error) {
      console.error('Error creating background task:', error);
      throw new Error(`Failed to create task: ${error.message || error.toString() || 'Unknown error'}`);
    }
  }

  /**
   * Process task using Netlify function
   * @param {string} taskId - Task ID to process
   */
  async processTaskAsync(taskId) {
    try {
      // Get task details
      const { data: task } = await supabase
        .from('background_tasks')
        .select('*')
        .eq('id', taskId)
        .single();

      if (!task) {
        throw new Error('Task not found');
      }

      // Call Netlify function to process the task
      const response = await fetch('/.netlify/functions/process-background-task', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          taskId: task.id,
          type: task.type,
          data: task.data
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to process task: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Task processed successfully:', result);

    } catch (error) {
      console.error('Error processing background task:', error);
      
      // Update task status to failed
      await supabase
        .from('background_tasks')
        .update({ 
          status: 'failed',
          error: error.message,
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId);
    }
  }

  /**
   * Get task status and details
   * @param {string} taskId - Task ID
   * @returns {Promise<object>} Task object
   */
  async getTask(taskId) {
    try {
      const { data: task, error } = await supabase
        .from('background_tasks')
        .select('*')
        .eq('id', taskId)
        .single();

      if (error) {
        throw new Error(`Failed to get task: ${error.message}`);
      }

      return task;
    } catch (error) {
      console.error('Error getting task:', error);
      throw error;
    }
  }

  /**
   * Get task results
   * @param {string} taskId - Task ID
   * @returns {Promise<object>} Task results
   */
  async getTaskResults(taskId) {
    try {
      const { data: task, error } = await supabase
        .from('background_tasks')
        .select('result')
        .eq('id', taskId)
        .single();

      if (error) {
        throw new Error(`Failed to get task results: ${error.message}`);
      }

      return task.result;
    } catch (error) {
      console.error('Error getting task results:', error);
      throw error;
    }
  }

  /**
   * Start website analysis as a background task
   * @param {Array} websites - Array of website URLs
   * @param {object} config - Analysis configuration
   * @returns {Promise<object>} Task object
   */
  async startWebsiteAnalysis(websites, config = {}) {
    return await this.createTask('website_analysis', {
      websites,
      config: {
        ...config,
        apiKey: config.apiKey || localStorage.getItem('apiKey'),
        analysisType: config.analysisType || 'comprehensive'
      }
    });
  }

  /**
   * Start email verification as a background task
   * @param {Array} emails - Array of email addresses
   * @param {object} config - Verification configuration
   * @returns {Promise<object>} Task object
   */
  async startEmailVerification(emails, config = {}) {
    return await this.createTask('email_verification', {
      emails,
      config: {
        ...config,
        reoonApiKey: config.reoonApiKey || import.meta.env.VITE_REOON_API_KEY
      }
    });
  }

  /**
   * Poll task status until completion
   * @param {string} taskId - Task ID
   * @param {function} onProgress - Progress callback
   * @param {number} pollInterval - Polling interval in ms (default: 2000)
   * @returns {Promise<object>} Final task result
   */
  async pollTaskUntilComplete(taskId, onProgress = null, pollInterval = 2000) {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const task = await this.getTask(taskId);
          
          if (onProgress) {
            onProgress(task);
          }

          if (task.status === 'completed') {
            resolve(task);
          } else if (task.status === 'failed') {
            reject(new Error(task.error || 'Task failed'));
          } else {
            // Continue polling
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }

  /**
   * Subscribe to task updates using Supabase realtime
   * @param {string} taskId - Task ID
   * @param {function} callback - Update callback
   * @returns {function} Unsubscribe function
   */
  subscribeToTaskUpdates(taskId, callback) {
    const subscription = supabase
      .channel(`task-${taskId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'background_tasks',
          filter: `id=eq.${taskId}`
        },
        (payload) => {
          callback(payload.new);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }

  /**
   * Get all tasks for the current user
   * @returns {Promise<Array>} Array of task objects
   */
  async getUserTasks() {
    try {
      const { data: tasks, error } = await supabase
        .from('background_tasks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to get user tasks: ${error.message}`);
      }

      return tasks || [];
    } catch (error) {
      console.error('Error getting user tasks:', error);
      throw error;
    }
  }

  /**
   * Cancel a background task
   * @param {string} taskId - Task ID to cancel
   * @returns {Promise<boolean>} Success status
   */
  async cancelTask(taskId) {
    try {
      const { error } = await supabase
        .from('background_tasks')
        .update({ 
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', taskId);

      if (error) {
        throw new Error(`Failed to cancel task: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error canceling background task:', error);
      throw error;
    }
  }
}

export const backgroundTaskService = new BackgroundTaskService();